import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CarrierService } from '../../../../../services/forwarding/carrier.service';
import { Carrier, CreateCarrierDto, UpdateCarrierDto, CARRIER_TYPES } from '../../../../../models/forwarding/carrier.model';

@Component({
  selector: 'app-carriers-list-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './carriers-list-tab.html',
  styleUrl: './carriers-list-tab.css'
})
export class CarriersListTabComponent implements OnInit, OnDestroy {
  showAddModal = false;
  showEditModal = false;
  carriers: Carrier[] = [];
  filteredCarriers: Carrier[] = [];
  searchTerm = '';
  private subscriptions = new Subscription();

  readonly carrierTypes = CARRIER_TYPES;

  formData: CreateCarrierDto = {
    code: '',
    name: '',
    carrierType: undefined,
    modes: [],
    regions: [],
    notes: '',
    isActive: true
  };

  editFormData: UpdateCarrierDto = {
    code: '',
    name: '',
    carrierType: undefined,
    modes: [],
    regions: [],
    notes: '',
    isActive: true
  };

  currentEditId: number | null = null;

  // Available modes and regions for selection
  availableModes = ['FCL', 'LCL', 'Air', 'RORO'];
  availableRegions = ['Asia', 'Europe', 'North America', 'South America', 'Africa', 'Oceania', 'Middle East'];

  // Custom region input
  customRegionInput = '';

  constructor(private carrierService: CarrierService) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.carrierService.getCarriers().subscribe(carriers => {
        this.carriers = carriers;
        this.filteredCarriers = carriers;
        console.log('[CarriersTab] Carriers updated:', carriers.length);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openAddCarrierModal(): void {
    this.showAddModal = true;
    this.resetForm();
    console.log('[CarriersTab] Add modal opened');
  }

  closeAddCarrierModal(): void {
    this.showAddModal = false;
    this.resetForm();
  }

  openEditCarrierModal(carrier: Carrier): void {
    this.showEditModal = true;
    this.currentEditId = carrier.id;
    this.editFormData = {
      code: carrier.code,
      name: carrier.name,
      carrierType: carrier.carrierType,
      modes: carrier.modes ? [...carrier.modes] : [],
      regions: carrier.regions ? [...carrier.regions] : [],
      notes: carrier.notes || '',
      isActive: carrier.isActive
    };
    console.log('[CarriersTab] Edit modal opened for carrier:', carrier.id);
  }

  closeEditCarrierModal(): void {
    this.showEditModal = false;
    this.currentEditId = null;
    this.editFormData = { code: '', name: '', modes: [], regions: [], notes: '', isActive: true };
  }

  filterCarriers(): void {
    const search = this.searchTerm.toLowerCase().trim();
    if (!search) {
      this.filteredCarriers = this.carriers;
      return;
    }

    this.filteredCarriers = this.carriers.filter(c =>
      c.code.toLowerCase().includes(search) ||
      c.name.toLowerCase().includes(search) ||
      (c.modes && c.modes.some(m => m.toLowerCase().includes(search))) ||
      (c.regions && c.regions.some(r => r.toLowerCase().includes(search)))
    );
  }

  createCarrier(): void {
    if (!this.formData.code || !this.formData.name) {
      alert('Please fill in all required fields (Code and Name)');
      return;
    }

    this.carrierService.create(this.formData).subscribe({
      next: (carrier) => {
        console.log('[CarriersTab] Carrier created successfully:', carrier);
        this.closeAddCarrierModal();
      },
      error: (error) => {
        console.error('[CarriersTab] Error creating carrier:', error);
        alert('Error creating carrier. Please try again.');
      }
    });
  }

  updateCarrier(): void {
    if (!this.currentEditId || !this.editFormData.code || !this.editFormData.name) {
      alert('Please fill in all required fields');
      return;
    }

    this.carrierService.update(this.currentEditId, this.editFormData).subscribe({
      next: (carrier) => {
        console.log('[CarriersTab] Carrier updated successfully:', carrier);
        this.closeEditCarrierModal();
      },
      error: (error) => {
        console.error('[CarriersTab] Error updating carrier:', error);
        alert('Error updating carrier. Please try again.');
      }
    });
  }

  deleteCarrier(id: number): void {
    if (!confirm('Are you sure you want to delete this carrier?')) {
      return;
    }

    this.carrierService.delete(id).subscribe({
      next: () => {
        console.log('[CarriersTab] Carrier deleted successfully');
      },
      error: (error) => {
        console.error('[CarriersTab] Error deleting carrier:', error);
        alert('Error deleting carrier. Please try again.');
      }
    });
  }

  toggleMode(mode: string, isEdit: boolean = false): void {
    const targetArray = isEdit ? this.editFormData.modes : this.formData.modes;
    if (!targetArray) return;

    const index = targetArray.indexOf(mode);
    if (index > -1) {
      targetArray.splice(index, 1);
    } else {
      targetArray.push(mode);
    }
  }

  toggleRegion(region: string, isEdit: boolean = false): void {
    const targetArray = isEdit ? this.editFormData.regions : this.formData.regions;
    if (!targetArray) return;

    const index = targetArray.indexOf(region);
    if (index > -1) {
      targetArray.splice(index, 1);
    } else {
      targetArray.push(region);
    }
  }

  isModeSelected(mode: string, isEdit: boolean = false): boolean {
    const targetArray = isEdit ? this.editFormData.modes : this.formData.modes;
    return targetArray ? targetArray.includes(mode) : false;
  }

  isRegionSelected(region: string, isEdit: boolean = false): boolean {
    const targetArray = isEdit ? this.editFormData.regions : this.formData.regions;
    return targetArray ? targetArray.includes(region) : false;
  }

  addCustomRegion(isEdit: boolean = false): void {
    const customRegion = this.customRegionInput.trim();
    if (!customRegion) {
      return;
    }

    const targetArray = isEdit ? this.editFormData.regions : this.formData.regions;
    if (!targetArray) return;

    // Check if region already exists (case insensitive)
    const exists = targetArray.some(r => r.toLowerCase() === customRegion.toLowerCase());
    if (exists) {
      alert('This region already exists');
      return;
    }

    // Add to selected regions
    targetArray.push(customRegion);

    // Clear input
    this.customRegionInput = '';

    console.log('[CarriersTab] Custom region added:', customRegion);
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getCarrierTypeLabel(type: string | undefined): string {
    if (!type) return '-';
    return this.carrierTypes.find(t => t.value === type)?.label ?? type;
  }

  private resetForm(): void {
    this.formData = {
      code: '',
      name: '',
      carrierType: undefined,
      modes: [],
      regions: [],
      notes: '',
      isActive: true
    };
    this.customRegionInput = '';
  }
}
